/**
 * 内部剪贴板：复制 / 剪切 / 粘贴。
 *
 * 用**序列化片段**存储而不是元素引用 —— 复制的内容要在文档被替换（打开新文件）后仍然可用，
 * 也避免持有游离节点。id 在**粘贴时**才重写（用当时文档里的既有 id 判重）。
 */
import { SVG_NS } from './dom';
import { translate } from './geom/matrix';
import { nextTransformAttribute } from './geom/handles';
import type { Command } from './history';
import { rewriteIdsInClone } from './ids';
import { makeStructuralPatch, snapshotOf, type Patch } from './patch';
import type { TransformDeps } from './ops/transform';

export interface PasteResult {
  cmd: Command;
  selection: Element[];
}

export interface Clipboard {
  size(): number;
  isEmpty(): boolean;
  copy(els: readonly Element[]): number;
  clear(): void;
  cut(deps: TransformDeps, els: readonly Element[]): Command;
  paste(deps: TransformDeps, parent: Element, dx?: number, dy?: number): PasteResult;
}

const serialize = (el: Element): string => new XMLSerializer().serializeToString(el);

/** 把片段解析成元素（包一层临时 <svg> 以保留命名空间） */
function fragmentToElement(xml: string, doc: Document): Element | null {
  const holder = new DOMParser().parseFromString(
    `<svg xmlns="${SVG_NS}" xmlns:xlink="http://www.w3.org/1999/xlink">${xml}</svg>`,
    'image/svg+xml',
  ).documentElement;
  const node = holder.firstElementChild;
  return node ? (doc.importNode(node, true) as Element) : null;
}

export function createClipboard(): Clipboard {
  let entries: string[] = [];

  return {
    size: () => entries.length,
    isEmpty: () => entries.length === 0,
    clear: () => {
      entries = [];
    },

    copy(els) {
      entries = els.map(serialize);
      return entries.length;
    },

    cut(deps, els) {
      // 剪切 = 复制到剪贴板 + 删除（删除本身是一条可撤销命令）
      this.copy(els);
      const patches: Patch[] = [];
      const byParent = new Map<Element, Element[]>();
      for (const el of els) {
        const parent = el.parentElement;
        if (!parent || el === deps.root) continue;
        const list = byParent.get(parent);
        if (list) list.push(el);
        else byParent.set(parent, [el]);
      }
      for (const [parent, list] of byParent) {
        const before = snapshotOf(parent);
        patches.push(
          makeStructuralPatch(parent, before, '剪切', () => {
            for (const el of list) if (el.parentElement === parent) parent.removeChild(el);
          }),
        );
      }
      return { label: '剪切', patches };
    },

    paste(deps, parent, dx = 12, dy = 12) {
      const doc = deps.root.ownerDocument;
      const plans: Element[] = [];
      for (const xml of entries) {
        const clone = fragmentToElement(xml, doc);
        if (!clone) continue;
        rewriteIdsInClone(clone, deps.root); // 未入树时重写，不会与自身冲突
        clone.setAttribute(
          'transform',
          nextTransformAttribute(clone.getAttribute('transform'), translate(dx, dy), deps.parentMatOf(clone)),
        );
        plans.push(clone);
      }
      if (plans.length === 0) return { cmd: { label: '粘贴', patches: [] }, selection: [] };

      const before = snapshotOf(parent);
      const cmd: Command = {
        label: '粘贴',
        patches: [
          makeStructuralPatch(parent, before, '粘贴', () => {
            for (const clone of plans) parent.appendChild(clone);
          }),
        ],
      };
      return { cmd, selection: plans };
    },
  };
}

export const clipboard = createClipboard();
